// Czech is a gendered language: "Spustila jsi kampaň?" is wrong for a man and
// "Spustil jsi" is wrong for a woman. The first three clients were women, so
// the q2 copy was written in the feminine and Pavel (F64, 2026-08-06) hit it on
// his own live run: "Dotaznik na me mluvi v zenskem rode, i kdyz jsem muz."
//
// One marker carries both forms through the locale files and the questionnaire
// definitions: {{g:masculine|feminine}} - optionally {{g:m|f|neutral}} when the
// two forms are too far apart to merge into one slashed word.
//
// English needs none of this, so the marker only ever appears in cs.json.

export type ClientGender = "f" | "m" | null;

const MARKER = /\{\{g:([^{}]*)\}\}/g;

/** The longest shared opening of two forms ("pripraven" for pripraveny/pripravena). */
function sharedPrefix(masculine: string, feminine: string): string {
  let index = 0;
  while (index < masculine.length && index < feminine.length && masculine[index] === feminine[index]) index += 1;
  return masculine.slice(0, index);
}

/**
 * "spustil" + "spustila" -> "spustil/a"; "pripraveny" + "pripravena" ->
 * "pripraveny/a". Returns null when the forms diverge too much for a slash to
 * read as one word (a whole extra word, a different stem); such a pair must
 * spell its neutral form out as the third segment instead.
 */
export function mergeGenderForms(masculine: string, feminine: string): string | null {
  const prefix = sharedPrefix(masculine, feminine);
  if (!prefix) return null;
  const masculineTail = masculine.slice(prefix.length);
  const feminineTail = feminine.slice(prefix.length);
  if (!feminineTail) return null;
  if (masculineTail.length > 4 || feminineTail.length > 4) return null;
  if (/\s/u.test(masculineTail) || /\s/u.test(feminineTail)) return null;
  return `${masculine}/${feminineTail}`;
}

export interface GenderSegments {
  masculine: string;
  feminine: string;
  /** Explicit form for an unknown gender; derived from the pair when absent. */
  neutral: string | null;
}

export function parseGenderSegments(raw: string): GenderSegments | null {
  const parts = raw.split("|");
  if (parts.length !== 2 && parts.length !== 3) return null;
  if (parts.some((part) => part.length === 0)) return null;
  return { masculine: parts[0], feminine: parts[1], neutral: parts[2] ?? null };
}

/** Every {{g:...}} marker in the text, in order. */
export function genderMarkers(text: string): GenderSegments[] {
  const found: GenderSegments[] = [];
  for (const match of text.matchAll(MARKER)) {
    const segments = parseGenderSegments(match[1]);
    if (segments) found.push(segments);
  }
  return found;
}

export function hasGenderMarker(text: string): boolean {
  return /\{\{g:/u.test(text);
}

/**
 * Resolves every {{g:...}} marker. A known gender picks its form; an unknown
 * one takes the explicit neutral segment, or the merged "spustil/a" shape.
 *
 * The last resort (an unmergeable pair with no neutral segment) joins both
 * full forms with a slash: clumsy to read, but true for every reader, unlike
 * silently falling back to the masculine. `assertGenderMarkersResolvable`
 * keeps that case out of the shipped copy.
 */
export function applyGender(text: string, gender: ClientGender): string {
  if (!hasGenderMarker(text)) return text;
  return text.replace(MARKER, (whole, raw: string) => {
    const segments = parseGenderSegments(raw);
    if (!segments) return whole;
    if (gender === "m") return segments.masculine;
    if (gender === "f") return segments.feminine;
    return segments.neutral
      ?? mergeGenderForms(segments.masculine, segments.feminine)
      ?? `${segments.masculine}/${segments.feminine}`;
  });
}

/**
 * Build-time guard: names every marker whose unknown-gender form would fall
 * through to the clumsy last resort. Used by the locale check and the tests.
 */
export function unresolvableGenderMarkers(text: string): GenderSegments[] {
  return genderMarkers(text).filter(
    (segments) => !segments.neutral && mergeGenderForms(segments.masculine, segments.feminine) === null
  );
}

/** Markers written with the wrong number of segments, e.g. {{g:jen jeden}}. */
export function malformedGenderMarkers(text: string): string[] {
  return [...text.matchAll(MARKER)].filter((match) => parseGenderSegments(match[1]) === null).map((match) => match[0]);
}

// Name-based first guess. Czech female surnames end in -ova/-a and female given
// names in -a/-e, but both rules have real exceptions, and some names are
// genuinely ambiguous. An honest null beats a confident mistake: the client
// then reads the neutral "spustil/a" and a partner can set the truth by hand.

const FEMALE_GIVEN_EXCEPTIONS = new Set([
  "dagmar", "ester", "esther", "ingrid", "karin", "miriam", "rut", "ruth", "doris",
  "nikol", "ellen", "sarah", "rachel", "vivien", "jasmin", "carmen", "iris", "sharon",
  "astrid", "solveig", "gwen", "marion", "helen", "kristin", "yasmin", "noemi"
]);

const MALE_GIVEN_EXCEPTIONS = new Set([
  "honza", "ondra", "standa", "franta", "pepa", "kuba", "jirka", "ruda", "vitek",
  "risa", "petja", "ilja", "sava", "kaja", "luca", "noa", "attila", "elia"
]);

/** Names a Czech reader cannot gender either: unisex here or abroad. */
const AMBIGUOUS_GIVEN = new Set([
  "nikola", "andrea", "sasa", "misa", "rene", "alex", "kim", "robin", "jean",
  "dominique", "chris", "sam", "jordan", "morgan", "casey", "andy", "nikita"
]);

function stripDiacritics(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/gu, "");
}

const TITLES = new Set([
  "ing", "mgr", "bc", "mudr", "judr", "phdr", "rndr", "mvdr", "paeddr", "dr", "prof", "doc", "mba", "dis"
]);

function nameTokens(fullName: string): string[] {
  return fullName
    .split(/[\s,]+/u)
    .map((token) => token.replace(/[.]/gu, "").trim())
    .filter((token) => token.length > 1 && !TITLES.has(stripDiacritics(token).toLowerCase()));
}

/**
 * Guesses the gender behind a client name. Returns null whenever the name does
 * not settle it - an empty or single-letter name, a unisex name, a foreign name
 * whose ending says nothing in Czech.
 */
export function guessGenderFromName(fullName: string | null | undefined): ClientGender {
  if (!fullName) return null;
  const tokens = nameTokens(fullName);
  if (!tokens.length) return null;

  // The surname is the stronger signal and it settles names the given name
  // cannot (Dagmar Novakova). The acute is the whole distinction here, so this
  // one rule reads the accent: female -ova / adjectival -a (Pokorna, Vesela)
  // against the many MALE surnames that end in a plain -a (Svoboda, Kalina,
  // Prochazka). Stripping diacritics first would call every Svoboda a woman.
  for (const token of tokens.slice(1)) {
    const surname = token.toLowerCase();
    if (surname.endsWith("ová") || surname.endsWith("á")) return "f";
  }

  const given = tokens[0];
  const plainGiven = stripDiacritics(given).toLowerCase();
  if (AMBIGUOUS_GIVEN.has(plainGiven)) return null;
  if (FEMALE_GIVEN_EXCEPTIONS.has(plainGiven)) return "f";
  if (MALE_GIVEN_EXCEPTIONS.has(plainGiven)) return "m";
  // Foreign endings that carry no Czech signal (Lucy, Naomi, Tony, Bobby).
  if (/[iyíý]$/u.test(given)) return null;
  if (/[aáeě]$/u.test(given)) return "f";
  return "m";
}

export function parseClientGender(value: unknown): ClientGender {
  return value === "f" || value === "m" ? value : null;
}
